import { NZ_AERODROMES } from '@/lib/aerodromes';
import type { Flight, LogbookEntry } from '@/lib/db';
import { formatLocalDate } from '@/lib/format';
import { normaliseRegistration } from '@/lib/parse';
import type { TrackCandidate } from '@/lib/sources';

import { entryTotalHours } from './columns';

/**
 * Scoring a flight a source knows about against an entry a pilot typed.
 *
 * `DATA-SOURCES.md` §4 specifies the four signals and their relative weight in
 * words — *registration is near-decisive, time overlap (allowing the track to
 * sit inside the entry), route endpoints within a few miles, same local
 * calendar date with a tolerance for midnight* — and this is that, as numbers.
 *
 * Three decisions in it are worth stating rather than reading out of the
 * arithmetic.
 *
 * **A signal that cannot be judged is not a signal that failed.** A hand-typed
 * entry very often has a date, an aircraft and some hours, and no off-block
 * time and no aerodromes at all — that is what transcribing from a paper
 * logbook looks like. Scoring those as zero would push every honest manual
 * entry below the threshold. So an unknown signal drops out of the weighting
 * entirely, and the UI says which ones actually carried the verdict.
 *
 * **A registration that disagrees is close to proof.** Two aircraft is two
 * flights, whatever else lines up, so a contradiction there vetoes the match
 * rather than being averaged away by three signals that happen to agree.
 *
 * **Nothing here decides anything.** The threshold picks which candidate is
 * labelled the suggested match. It never applies one: `PLAN.md` §6 Phase 5 is
 * explicit that the pilot reviews and confirms, and that the entry picks up
 * derived fields only on approval.
 */

export type MatchSignalId = 'registration' | 'date' | 'time' | 'route';

export interface MatchSignal {
  id: MatchSignalId;
  label: string;
  /** `0`–`1`, or `null` when there was nothing on one side to compare. */
  score: number | null;
  weight: number;
  /** What was actually compared, in the pilot's words. */
  detail: string;
}

export type MatchConfidence = 'strong' | 'possible' | 'weak';

export interface MatchScore {
  /** `0`–`1`, weighted across the signals that could be judged. */
  score: number;
  confidence: MatchConfidence;
  signals: MatchSignal[];
  /** True when a signal contradicted outright — a different aeroplane. */
  vetoed: boolean;
}

const WEIGHTS: Record<MatchSignalId, number> = {
  registration: 0.4,
  date: 0.25,
  time: 0.2,
  route: 0.15,
};

/** At or above this, the top candidate is offered as *the* suggested match. */
export const STRONG_MATCH = 0.8;

/** Below this, a candidate is listed but never suggested. */
export const POSSIBLE_MATCH = 0.5;

/** A flight that crossed midnight is logged on one of the two days; either is fine. */
const MIDNIGHT_TOLERANCE_SCORE = 0.4;

export interface ScoreOptions {
  /** The pilot's timezone setting, so "same day" means their day. */
  timeZone?: string;
}

function candidateHours(candidate: TrackCandidate): number | undefined {
  if (candidate.startTime === undefined || candidate.endTime === undefined) {
    return undefined;
  }
  const ms = Date.parse(candidate.endTime) - Date.parse(candidate.startTime);
  return Number.isFinite(ms) && ms > 0 ? ms / 3_600_000 : undefined;
}

/** Whole days between a stored calendar date and a candidate's start, in the pilot's own zone. */
function dayGap(date: string, startTime: string, timeZone: string | undefined): number {
  const entryDay = Date.parse(`${date}T00:00:00Z`);
  const candidateDay = Date.parse(
    `${formatLocalDate(startTime, { format: 'iso', ...(timeZone === undefined ? {} : { timeZone }) })}T00:00:00Z`,
  );
  if (Number.isNaN(entryDay) || Number.isNaN(candidateDay)) {
    return Number.MAX_SAFE_INTEGER;
  }
  return Math.round(Math.abs(candidateDay - entryDay) / 86_400_000);
}

/**
 * An aerodrome cell as a comparable code.
 *
 * A pilot types `NZWK`, or `Whakatane`, or `WHK`; a source answers with an
 * ICAO code. Resolving a typed name through the bundled table means the two
 * are comparable without asking the pilot to have typed the code.
 */
function aerodromeCode(raw: string | undefined): string | undefined {
  const text = raw?.trim();
  if (text === undefined || text === '') {
    return undefined;
  }
  const upper = text.toUpperCase();
  const byName = NZ_AERODROMES.find(
    (aerodrome) => aerodrome.name?.toUpperCase() === upper || aerodrome.icao === upper,
  );
  return byName?.icao ?? upper;
}

/** Fraction of the shorter interval that two intervals share. */
function overlap(aStart: number, aEnd: number, bStart: number, bEnd: number): number {
  const shared = Math.min(aEnd, bEnd) - Math.max(aStart, bStart);
  if (shared <= 0) {
    return 0;
  }
  const shorter = Math.min(aEnd - aStart, bEnd - bStart);
  return shorter <= 0 ? 0 : Math.min(1, shared / shorter);
}

function registrationSignal(
  entry: LogbookEntry,
  candidate: TrackCandidate,
): { signal: MatchSignal; veto: boolean } {
  const mine =
    entry.registration === undefined ? undefined : normaliseRegistration(entry.registration);
  const theirs =
    candidate.registration === undefined
      ? undefined
      : normaliseRegistration(candidate.registration);

  if (mine === undefined || theirs === undefined || mine === '' || theirs === '') {
    return {
      signal: {
        id: 'registration',
        label: 'Registration',
        score: null,
        weight: WEIGHTS.registration,
        detail:
          mine === undefined || mine === ''
            ? 'Entry has no registration'
            : `Entry ${mine}. This source gave no registration.`,
      },
      veto: false,
    };
  }

  const same = mine === theirs;
  return {
    signal: {
      id: 'registration',
      label: 'Registration',
      score: same ? 1 : 0,
      weight: WEIGHTS.registration,
      detail: same ? `Both ${mine}` : `Entry ${mine}; this flight was ${theirs}`,
    },
    veto: !same,
  };
}

function dateSignal(
  entry: LogbookEntry,
  candidate: TrackCandidate,
  timeZone: string | undefined,
): MatchSignal {
  if (candidate.startTime === undefined) {
    return {
      id: 'date',
      label: 'Date',
      score: null,
      weight: WEIGHTS.date,
      detail: 'This source gave no time for the flight',
    };
  }
  const gap = dayGap(entry.date, candidate.startTime, timeZone);
  const flown = formatLocalDate(candidate.startTime, timeZone === undefined ? {} : { timeZone });
  if (gap === 0) {
    return {
      id: 'date',
      label: 'Date',
      score: 1,
      weight: WEIGHTS.date,
      detail: `Both ${flown}`,
    };
  }
  if (gap === 1) {
    return {
      id: 'date',
      label: 'Date',
      score: MIDNIGHT_TOLERANCE_SCORE,
      weight: WEIGHTS.date,
      detail: `A day apart. Entry ${entry.date}; this flew ${flown}. A flight across midnight is logged on one day or the other.`,
    };
  }
  return {
    id: 'date',
    label: 'Date',
    score: 0,
    weight: WEIGHTS.date,
    detail: `${gap} days apart. Entry ${entry.date}; this flew ${flown}.`,
  };
}

function timeSignal(
  entry: LogbookEntry,
  flight: Flight | undefined,
  candidate: TrackCandidate,
): MatchSignal {
  const base = { id: 'time' as const, label: 'Time', weight: WEIGHTS.time };

  const candidateStart = candidate.startTime === undefined ? NaN : Date.parse(candidate.startTime);
  const candidateEnd = candidate.endTime === undefined ? NaN : Date.parse(candidate.endTime);

  /*
   * When the entry carries real times, compare intervals — and note that a
   * track sitting *inside* the entry scores 1, which is the normal case:
   * ADS-B starts after the takeoff roll and stops before the aircraft is
   * parked, so the track is always the shorter of the two.
   */
  const entryStart = Date.parse(flight?.takeoff ?? flight?.offBlock ?? '');
  const entryEnd = Date.parse(flight?.landing ?? flight?.onBlock ?? '');
  if (
    Number.isFinite(entryStart) &&
    Number.isFinite(entryEnd) &&
    Number.isFinite(candidateStart) &&
    Number.isFinite(candidateEnd)
  ) {
    const fraction = overlap(entryStart, entryEnd, candidateStart, candidateEnd);
    return {
      ...base,
      score: fraction,
      detail: `${Math.round(fraction * 100)}% of the shorter interval is shared`,
    };
  }

  /*
   * Otherwise fall back on the one time figure a transcribed entry always has:
   * the hours flown. It is a weaker signal than an interval and it is scored
   * as one, but "you logged 1:12 and this flight was 1:09" is real evidence,
   * and refusing to use it would leave every paper-logbook entry unjudged on
   * time at all.
   */
  const logged = entryTotalHours(entry);
  const flown = candidateHours(candidate);
  if (logged > 0 && flown !== undefined) {
    const difference = Math.abs(logged - flown);
    // Scaled against the flight's own length, so six minutes out matters more
    // on a circuit detail than on a three-hour cross-country.
    const score = Math.max(0, 1 - difference / Math.max(logged, 0.5));
    return {
      ...base,
      score,
      detail: `Entry ${logged.toFixed(1)} hr; this flight was airborne ${flown.toFixed(1)} hr`,
    };
  }

  return {
    ...base,
    score: null,
    detail: logged > 0 ? 'This source gave no times to compare' : 'Entry has no times or hours',
  };
}

function routeSignal(entry: LogbookEntry, candidate: TrackCandidate): MatchSignal {
  const base = { id: 'route' as const, label: 'Route', weight: WEIGHTS.route };

  const myFrom = aerodromeCode(entry.from);
  const myTo = aerodromeCode(entry.to);
  const theirFrom = aerodromeCode(candidate.departure);
  const theirTo = aerodromeCode(candidate.arrival);

  const comparable: boolean[] = [];
  if (myFrom !== undefined && theirFrom !== undefined) {
    comparable.push(myFrom === theirFrom);
  }
  if (myTo !== undefined && theirTo !== undefined) {
    comparable.push(myTo === theirTo);
  }

  if (comparable.length === 0) {
    return {
      ...base,
      score: null,
      detail:
        myFrom === undefined && myTo === undefined
          ? 'Entry has no aerodromes'
          : 'This source gave no route for the flight',
    };
  }

  const agreed = comparable.filter(Boolean).length;
  return {
    ...base,
    score: agreed / comparable.length,
    detail: `Entry ${myFrom ?? '?'} → ${myTo ?? '?'}; this flight ${theirFrom ?? '?'} → ${theirTo ?? '?'}`,
  };
}

export function scoreCandidate(
  entry: LogbookEntry,
  flight: Flight | undefined,
  candidate: TrackCandidate,
  options: ScoreOptions = {},
): MatchScore {
  const registration = registrationSignal(entry, candidate);
  const signals: MatchSignal[] = [
    registration.signal,
    dateSignal(entry, candidate, options.timeZone),
    timeSignal(entry, flight, candidate),
    routeSignal(entry, candidate),
  ];

  const judged = signals.filter(
    (signal): signal is MatchSignal & { score: number } => signal.score !== null,
  );
  const totalWeight = judged.reduce((sum, signal) => sum + signal.weight, 0);
  const score =
    totalWeight === 0
      ? 0
      : judged.reduce((sum, signal) => sum + signal.score * signal.weight, 0) / totalWeight;

  const confidence: MatchConfidence = registration.veto
    ? 'weak'
    : score >= STRONG_MATCH
      ? 'strong'
      : score >= POSSIBLE_MATCH
        ? 'possible'
        : 'weak';

  return { score, confidence, signals, vetoed: registration.veto };
}

export const CONFIDENCE_LABEL: Record<MatchConfidence, string> = {
  strong: 'Very likely this flight',
  possible: 'Possibly this flight',
  weak: 'Probably not this flight',
};
